import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { 
  Plus, Check, X, RefreshCw, Calendar, Trash2, ChevronLeft, ChevronRight, AlertTriangle, Edit2, Save, Ban, RotateCcw, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown, EyeOff, Eye, Banknote, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../App';
import { API } from '../../utils/config';


// === COMPONENTE DA INCASSARE ===
const DaIncassareSection = ({ token, isAdmin }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/booking-register/da-incassare`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(data.entries || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const markCollected = async (entryId, amount) => {
    try {
      const res = await fetch(`${API}/api/booking-register/${entryId}/mark-collected`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ amount })
      });
      if (res.ok) {
        toast.success('Segnato come incassato');
        setEntries(prev => prev.filter(e => e.id !== entryId));
      }
    } catch (e) { toast.error('Errore'); }
  };

  const formatDate = (d) => d ? d.split('-').reverse().join('/') : '-';
  const fmt = (n) => (n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  // Raggruppa per data incasso
  const grouped = entries.reduce((acc, e) => {
    const d = e.collection_date || 'Senza data';
    if (!acc[d]) acc[d] = [];
    acc[d].push(e);
    return acc;
  }, {});

  const today = new Date().toISOString().split('T')[0];
  const overdue = entries.filter(e => e.collection_date && e.collection_date < today);
  const dueToday = entries.filter(e => e.collection_date === today);

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="da-incassare-section">
      <CardHeader className="pb-2 cursor-pointer" onClick={() => { if (!expanded) load(); setExpanded(!expanded); }}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Banknote className="h-5 w-5 text-amber-600" />
            Da Incassare
            {entries.length > 0 && <Badge className="bg-amber-500 text-white">{entries.length}</Badge>}
            {overdue.length > 0 && <Badge className="bg-red-500 text-white ml-1">{overdue.length} scadute</Badge>}
          </CardTitle>
          <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </CardHeader>
      {expanded && (
        <CardContent>
          {loading ? <p className="text-sm text-gray-500">Caricamento...</p> : entries.length === 0 ? (
            <p className="text-sm text-green-600 font-medium">Tutto incassato!</p>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-4 text-sm">
                <span className="text-red-600 font-medium">Scadute: {overdue.length}</span>
                <span className="text-amber-600 font-medium">Oggi: {dueToday.length}</span>
                <span className="text-gray-600">Totale: {entries.length}</span>
                <span className="font-bold">Importo: {fmt(entries.reduce((s, e) => s + (e.total_price || 0), 0))}</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs border-collapse">
                  <thead className="bg-amber-100">
                    <tr>
                      <th className="px-2 py-1.5 text-left border-b">IMMOBILE</th>
                      <th className="px-2 py-1.5 text-left border-b">CODICE</th>
                      <th className="px-2 py-1.5 text-center border-b">PIATT.</th>
                      <th className="px-2 py-1.5 text-center border-b">DAL</th>
                      <th className="px-2 py-1.5 text-center border-b">INCASSO</th>
                      <th className="px-2 py-1.5 text-right border-b">LOCAZ.</th>
                      <th className="px-2 py-1.5 text-right border-b bg-blue-50 text-blue-800">TOTALE</th>
                      <th className="px-2 py-1.5 text-center border-b">AZIONE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.keys(grouped).sort().map(dateKey => (
                      <React.Fragment key={dateKey}>
                        <tr className="bg-amber-50">
                          <td colSpan={8} className="px-2 py-1 text-xs font-bold text-amber-800 border-t">
                            {dateKey < today ? '🔴' : dateKey === today ? '🟡' : '🟢'} {formatDate(dateKey)} ({grouped[dateKey].length} pren.)
                          </td>
                        </tr>
                        {grouped[dateKey].map(entry => (
                          <tr key={entry.id} className={`border-b hover:bg-amber-50/50 ${entry.collection_date < today ? 'bg-red-50' : ''}`}>
                            <td className="px-2 py-1.5 font-medium">{entry.property_name || '-'}</td>
                            <td className="px-2 py-1.5 font-mono text-[10px]">{entry.booking_code}</td>
                            <td className="px-2 py-1.5 text-center">{entry.platform}</td>
                            <td className="px-2 py-1.5 text-center">{formatDate(entry.check_in)}</td>
                            <td className="px-2 py-1.5 text-center font-medium">{formatDate(entry.collection_date)}</td>
                            <td className="px-2 py-1.5 text-right font-medium">{fmt(entry.total_price)}</td>
                            <td className="px-2 py-1.5 text-right font-medium bg-blue-50/30 text-blue-800">{fmt(entry.total_price_full || entry.total_price)}</td>
                            <td className="px-2 py-1.5 text-center">
                              <Button 
                                size="sm" 
                                variant="outline"
                                className="h-7 px-2 text-xs border-green-300 text-green-700 hover:bg-green-50 touch-manipulation"
                                onClick={() => markCollected(entry.id, entry.total_price)}
                                data-testid={`mark-collected-${entry.id}`}
                              >
                                <CheckCircle className="h-3 w-3 mr-1" /> Incassato
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};


// === COMPONENTE ANOMALIE FINANZIARIE ===
const AnomaliesSection = ({ token, year }) => {
  const [anomalies, setAnomalies] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [filterSeverity, setFilterSeverity] = useState('all');
  const [sortField, setSortField] = useState('margin_pct');
  const [sortDir, setSortDir] = useState('asc');

  const loadAnomalies = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/booking-register/anomalies?year=${year}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setAnomalies(await res.json());
      else toast.error('Errore caricamento anomalie');
    } catch (e) { toast.error('Errore: ' + e.message); }
    setLoading(false);
  };

  useEffect(() => {
    if (expanded && !anomalies) loadAnomalies();
  }, [expanded, year]);

  const filtered = React.useMemo(() => {
    if (!anomalies?.anomalies) return [];
    let list = [...anomalies.anomalies];
    if (filterSeverity !== 'all') list = list.filter(a => a.severity === filterSeverity);
    list.sort((a, b) => {
      const av = a[sortField] ?? 0, bv = b[sortField] ?? 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return list;
  }, [anomalies, filterSeverity, sortField, sortDir]);

  const fmt = (v) => v != null ? `€${Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  const pctFmt = (v) => v != null ? `${v.toFixed(2)}%` : '-';

  const SortHeader = ({ field, children }) => (
    <th className="px-2 py-2 text-xs font-semibold text-left cursor-pointer hover:bg-slate-100 select-none whitespace-nowrap"
      onClick={() => { if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(field); setSortDir('asc'); } }}>
      {children} {sortField === field && (sortDir === 'asc' ? ' ↑' : ' ↓')}
    </th>
  );

  return (
    <Card className="border-amber-200" data-testid="anomalies-section">
      <CardHeader className="py-3 cursor-pointer" onClick={() => setExpanded(!expanded)}>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-500" />
            Anomalie Finanziarie {year}
            {anomalies && (
              <span className="flex gap-1.5 ml-2">
                {anomalies.critical_count > 0 && (
                  <Badge variant="destructive" className="text-xs">{anomalies.critical_count} critiche</Badge>
                )}
                {anomalies.warning_count > 0 && (
                  <Badge className="bg-amber-100 text-amber-800 text-xs">{anomalies.warning_count} sotto media</Badge>
                )}
              </span>
            )}
          </CardTitle>
          <ChevronDown className={`h-5 w-5 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </CardHeader>
      {expanded && (
        <CardContent className="pt-0">
          {loading ? (
            <div className="text-center py-8 text-slate-400">Analisi in corso...</div>
          ) : anomalies ? (
            <div className="space-y-3">
              {/* Stats riepilogative */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-slate-800">{anomalies.total_bookings}</div>
                  <div className="text-xs text-slate-500">Prenotazioni analizzate</div>
                </div>
                <div className="bg-blue-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-blue-700">{pctFmt(anomalies.avg_margin_pct)}</div>
                  <div className="text-xs text-blue-600">Marginalità media HW</div>
                </div>
                <div className="bg-red-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-red-700">{anomalies.critical_count}</div>
                  <div className="text-xs text-red-600">Margine negativo</div>
                </div>
                <div className="bg-amber-50 rounded-lg p-3 text-center">
                  <div className="text-2xl font-bold text-amber-700">{anomalies.warning_count}</div>
                  <div className="text-xs text-amber-600">Sotto la media</div>
                </div>
              </div>

              {/* Filtri */}
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500">Filtra:</span>
                {['all', 'critical', 'warning'].map(s => (
                  <button key={s} onClick={() => setFilterSeverity(s)}
                    className={`px-3 py-1 text-xs rounded-full border transition-colors ${
                      filterSeverity === s
                        ? s === 'critical' ? 'bg-red-100 text-red-800 border-red-300'
                          : s === 'warning' ? 'bg-amber-100 text-amber-800 border-amber-300'
                          : 'bg-slate-200 text-slate-800 border-slate-300'
                        : 'bg-white text-slate-500 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid={`anomaly-filter-${s}`}>
                    {s === 'all' ? `Tutte (${anomalies.anomalies.length})` : s === 'critical' ? `Critiche (${anomalies.critical_count})` : `Warning (${anomalies.warning_count})`}
                  </button>
                ))}
                <button onClick={loadAnomalies} className="ml-auto p-1.5 hover:bg-slate-100 rounded" title="Aggiorna">
                  <RefreshCw className="h-4 w-4 text-slate-400" />
                </button>
              </div>

              {/* Tabella anomalie */}
              {filtered.length > 0 ? (
                <div className="overflow-x-auto border rounded-lg max-h-[400px] overflow-y-auto">
                  <table className="w-full text-sm" data-testid="anomalies-table">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-xs font-semibold text-left w-8"></th>
                        <SortHeader field="booking_code">Codice</SortHeader>
                        <SortHeader field="property_name">Immobile</SortHeader>
                        <th className="px-2 py-2 text-xs font-semibold text-left">Piattaforma</th>
                        <th className="px-2 py-2 text-xs font-semibold text-left">Check-in</th>
                        <SortHeader field="total_price">Locaz.</SortHeader>
                        <th className="px-2 py-1.5 text-right border-b border-slate-200 text-xs font-semibold bg-blue-50 text-blue-800 cursor-default">Totale</th>
                        <SortHeader field="hw_margin">DIFF</SortHeader>
                        <SortHeader field="margin_pct">Marg. %</SortHeader>
                        <SortHeader field="deviation">Scost.</SortHeader>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.slice(0, 200).map((a, i) => (
                        <tr key={a.id || i} className={`border-t ${a.severity === 'critical' ? 'bg-red-50/50' : 'bg-amber-50/30'} hover:bg-slate-50`}>
                          <td className="px-2 py-1.5">
                            {a.severity === 'critical' 
                              ? <span className="inline-block w-2.5 h-2.5 rounded-full bg-red-500" title="Margine negativo"></span>
                              : <span className="inline-block w-2.5 h-2.5 rounded-full bg-amber-400" title="Sotto media"></span>
                            }
                          </td>
                          <td className="px-2 py-1.5 text-xs font-mono">{a.booking_code}</td>
                          <td className="px-2 py-1.5 text-xs max-w-[180px] truncate" title={a.property_name}>
                            {a.property_name}
                            {a.property_code && <span className="text-slate-400 ml-1">({a.property_code})</span>}
                          </td>
                          <td className="px-2 py-1.5 text-xs capitalize">{a.platform}</td>
                          <td className="px-2 py-1.5 text-xs">{a.check_in ? new Date(a.check_in).toLocaleDateString('it-IT') : '-'}</td>
                          <td className="px-2 py-1.5 text-xs text-right font-medium">{fmt(a.total_price)}</td>
                          <td className="px-2 py-1.5 text-xs text-right font-medium bg-blue-50/30 text-blue-800">{fmt(a.total_price_full || a.total_price)}</td>
                          <td className={`px-2 py-1.5 text-xs text-right font-bold ${a.hw_margin < 0 ? 'text-red-700' : 'text-green-700'}`}>
                            {fmt(a.hw_margin)}
                          </td>
                          <td className={`px-2 py-1.5 text-xs text-right font-bold ${a.margin_pct < 0 ? 'text-red-700' : a.margin_pct < anomalies.avg_margin_pct ? 'text-amber-700' : 'text-green-700'}`}>
                            {pctFmt(a.margin_pct)}
                          </td>
                          <td className="px-2 py-1.5 text-xs text-right text-slate-500">
                            {a.deviation > 0 ? '+' : ''}{pctFmt(a.deviation)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {filtered.length > 200 && (
                    <div className="text-center py-2 text-xs text-slate-400 bg-slate-50 border-t">
                      Mostrate 200 di {filtered.length} anomalie
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-6 text-slate-400 text-sm">
                  Nessuna anomalia trovata con i filtri selezionati
                </div>
              )}
            </div>
          ) : null}
        </CardContent>
      )}
    </Card>
  );
};


const PLATFORMS = [
  { value: 'booking', label: 'Booking' },
  { value: 'airbnb', label: 'Airbnb' },
  { value: 'interhome', label: 'Interhome' },
  { value: 'atraveo', label: 'Atraveo' },
  { value: 'holiday_world', label: 'Holiday World' },
  { value: 'other', label: 'Altro' }
];

const RegistroPrenotazioni = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const token = localStorage.getItem('hw_token');
  
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newRow, setNewRow] = useState(null);
  const [editingRow, setEditingRow] = useState(null);
  const [monthlyTotals, setMonthlyTotals] = useState(null);
  const [yearlyTotals, setYearlyTotals] = useState(null);
  const [populating, setPopulating] = useState(false);
  const [dateMode, setDateMode] = useState('booking_date'); // 'booking_date' or 'check_in'
  const [showCancelled, setShowCancelled] = useState(false);
  const [sortCol, setSortCol] = useState(null); // null | 'property_name' | 'booking_code' | ...
  const [sortDir, setSortDir] = useState('asc'); // 'asc' | 'desc'
  
  // Property list for dropdown
  const [allProperties, setAllProperties] = useState([]);
  const [propertyFilter, setPropertyFilter] = useState('');
  const [propertyDropdownOpen, setPropertyDropdownOpen] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 320 });

  // Load all properties once
  useEffect(() => {
    const loadProperties = async () => {
      try {
        const res = await fetch(`${API}/api/booking-register/properties/search?q=`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) {
          const data = await res.json();
          setAllProperties(data);
        }
      } catch (e) { console.error('Errore caricamento immobili:', e); }
    };
    if (token) loadProperties();
  }, [token]);

  const emptyRow = {
    property_id: '', property_name: '', property_code: '', max_guests: 0,
    apply_submultiples: false, available_submultiples: [],
    booking_code: '', tariff_type: 'N', submultiple: '',
    platform: 'booking', check_in: '', check_out: '',
    total_price: '', collected: '', to_collect: '', owner_share: '',
    cleaning_cost: '', cleaning_where: 'in_total',
    tourist_tax: '', tourist_tax_where: 'in_total'
  };

  const getSubmultipleOptions = (rowData) => {
    const maxGuests = rowData.max_guests || 0;
    if (!maxGuests || maxGuests <= 0) return [];

    // If property has explicit submultiples configured, use them
    if (rowData.apply_submultiples && Array.isArray(rowData.available_submultiples) && rowData.available_submultiples.length > 0) {
      const subs = [...rowData.available_submultiples].sort((a, b) => b - a);
      const options = [];
      for (const s of subs) {
        if (s >= maxGuests) {
          options.push({ value: '', label: `${maxGuests} (max)` });
        } else {
          options.push({ value: s.toString(), label: `/S${s}` });
        }
      }
      // Ensure max option exists
      if (!options.some(o => o.value === '')) {
        options.unshift({ value: '', label: `${maxGuests} (max)` });
      }
      return options;
    }

    // Fallback: generic algorithm
    const options = [{ value: '', label: `${maxGuests} (max)` }];
    for (let i = maxGuests - 2; i >= 2; i -= 2) {
      options.push({ value: i.toString(), label: `/S${i}` });
    }
    return options;
  };

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/booking-register?entry_date=${selectedDate}&date_mode=${dateMode}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setEntries(Array.isArray(data) ? data : data.entries || []);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [selectedDate, token, dateMode]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  // Fetch monthly totals for admin
  useEffect(() => {
    if (!isAdmin || !token) return;
    const month = selectedDate.substring(0, 7);
    const year = selectedDate.substring(0, 4);
    const fetchSummaries = async () => {
      try {
        const [mRes, yRes] = await Promise.all([
          fetch(`${API}/api/booking-register/summary/monthly?month=${month}`, { headers: { Authorization: `Bearer ${token}` } }),
          fetch(`${API}/api/booking-register/summary/yearly?year=${year}`, { headers: { Authorization: `Bearer ${token}` } })
        ]);
        if (mRes.ok) setMonthlyTotals(await mRes.json());
        if (yRes.ok) setYearlyTotals(await yRes.json());
      } catch (e) { console.error('Errore totali:', e); }
    };
    fetchSummaries();
  }, [selectedDate, token, isAdmin, entries]);

  const populateFromBookings = async () => {
    const modeLabel = dateMode === 'check_in' ? 'data di check-in' : 'data di prenotazione';
    if (!window.confirm(`Popolare il registro con le prenotazioni per ${modeLabel}? Le prenotazioni esistenti verranno aggiornate, quelle nuove create.`)) return;
    setPopulating(true);
    try {
      const year = selectedDate.substring(0, 4);
      const res = await fetch(`${API}/api/booking-register/populate-from-bookings`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ year, date_mode: dateMode })
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Popolamento avviato in background');
        // Poll for completion
        if (data.task_id) {
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await fetch(`${API}/api/booking-register/populate-status/${data.task_id}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              if (statusRes.ok) {
                const statusData = await statusRes.json();
                if (statusData.status === 'completed') {
                  clearInterval(pollInterval);
                  const r = statusData.results || {};
                  toast.success(`Completato: ${r.created || 0} create, ${r.updated || 0} aggiornate, ${r.overlap_blocked || 0} bloccate per sovrapposizione`);
                  setPopulating(false);
                  fetchEntries();
                }
              }
            } catch {}
          }, 3000);
          setTimeout(() => clearInterval(pollInterval), 120000);
        } else {
          fetchEntries();
        }
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Errore');
      }
    } catch (e) { toast.error('Errore: ' + e.message); }
    setPopulating(false);
  };

  const selectProperty = (property, isNewRow) => {
    if (isNewRow) {
      setNewRow(prev => ({
        ...prev, property_id: property.id, property_name: property.name,
        property_code: property.property_code, max_guests: property.max_guests || 0,
        apply_submultiples: property.apply_submultiples || false,
        available_submultiples: property.available_submultiples || [],
        cleaning_cost: property.cleaning_cost?.toString() || '', submultiple: ''
      }));
    } else if (editingRow !== null) {
      const updated = [...entries];
      updated[editingRow] = {
        ...updated[editingRow], property_id: property.id, property_name: property.name,
        property_code: property.property_code, max_guests: property.max_guests || 0,
        apply_submultiples: property.apply_submultiples || false,
        available_submultiples: property.available_submultiples || [],
        cleaning_cost: property.cleaning_cost?.toString() || updated[editingRow].cleaning_cost,
        submultiple: ''
      };
      setEntries(updated);
    }
    setPropertyDropdownOpen(null);
    setPropertyFilter('');
  };

  const calcCollectionDate = (platform, tariffType, entryDate, checkIn) => {
    if (!checkIn || !entryDate) return entryDate || '';
    const ci = new Date(checkIn);
    const ed = new Date(entryDate);
    const addDays = (d, n) => { const r = new Date(d); r.setDate(r.getDate() + n); return r.toISOString().split('T')[0]; };
    if (platform === 'airbnb') return addDays(ci, 3);
    if (platform === 'booking') return tariffType === 'N' ? entryDate : addDays(ci, 1);
    if (platform === 'holiday_world') {
      const daysBefore = Math.floor((ci - ed) / 86400000);
      return daysBefore > 30 ? addDays(ci, -30) : entryDate;
    }
    if (platform === 'atraveo' || platform === 'interhome') {
      const d = addDays(ci, -7);
      return d >= entryDate ? d : entryDate;
    }
    return entryDate;
  };

  const updateNewRow = (field, value) => {
    setNewRow(prev => {
      const next = { ...prev, [field]: value };
      // Auto-calcola collection_date quando cambiano piattaforma, tariffa, check-in
      if (['platform', 'tariff_type', 'check_in'].includes(field)) {
        next.collection_date = calcCollectionDate(next.platform, next.tariff_type, selectedDate, next.check_in);
      }
      return next;
    });
  };
  
  const updateEditingRow = (field, value) => {
    const updated = [...entries];
    updated[editingRow] = { ...updated[editingRow], [field]: value };
    setEntries(updated);
  };

  const saveRow = async (rowData, isNew) => {
    console.log('[SaveRow] START', { isNew, property_id: rowData.property_id, id: rowData.id });
    if (!rowData.property_id) { toast.error('Seleziona un immobile'); return; }
    const url = isNew ? `${API}/api/booking-register` : `${API}/api/booking-register/${rowData.id}`;
    const cleanDate = (v) => (v && v.length >= 10) ? v : null;
    const payload = isNew ? {
      property_id: rowData.property_id,
      booking_code: rowData.booking_code || '',
      platform: rowData.platform || 'other',
      tariff_type: rowData.tariff_type || 'N',
      submultiple: rowData.submultiple || '',
      check_in: cleanDate(rowData.check_in),
      check_out: cleanDate(rowData.check_out),
      collection_date: cleanDate(rowData.collection_date),
      total_price: parseFloat(rowData.total_price) || 0,
      collected: parseFloat(rowData.collected) || 0,
      to_collect: parseFloat(rowData.to_collect) || 0,
      owner_share: parseFloat(rowData.owner_share) || 0,
      cleaning_cost: parseFloat(rowData.cleaning_cost) || 0,
      cleaning_where: rowData.cleaning_where || 'in_total',
      tourist_tax: parseFloat(rowData.tourist_tax) || 0,
      tourist_tax_where: rowData.tourist_tax_where || 'in_total',
      entry_date: selectedDate
    } : {
      booking_code: rowData.booking_code || null,
      platform: rowData.platform || null,
      tariff_type: rowData.tariff_type || null,
      submultiple: String(rowData.submultiple || ''),
      check_in: cleanDate(rowData.check_in),
      check_out: cleanDate(rowData.check_out),
      collection_date: cleanDate(rowData.collection_date),
      total_price: parseFloat(rowData.total_price) || 0,
      collected: parseFloat(rowData.collected) || 0,
      to_collect: parseFloat(rowData.to_collect) || 0,
      owner_share: parseFloat(rowData.owner_share) || 0,
      cleaning_cost: parseFloat(rowData.cleaning_cost) || 0,
      cleaning_where: rowData.cleaning_where || null,
      tourist_tax: parseFloat(rowData.tourist_tax) || 0,
      tourist_tax_where: rowData.tourist_tax_where || null
    };
    // Remove null/undefined to let backend use defaults
    const cleanPayload = Object.fromEntries(Object.entries(payload).filter(([_, v]) => v !== null && v !== undefined));
    console.log('[SaveRow] PAYLOAD', JSON.stringify(cleanPayload));
    try {
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(cleanPayload)
      });
      console.log('[SaveRow] Response status:', res.status);
      if (res.ok) {
        const result = await res.json();
        toast.success(isNew ? 'Prenotazione aggiunta' : 'Salvata');
        if (result.has_anomaly) toast.error('Anomalia: Margine negativo!', { duration: 5000 });
        if (isNew) setNewRow(null); else setEditingRow(null);
        fetchEntries();
      } else {
        let errorMsg = `HTTP ${res.status}`;
        try {
          const error = await res.json();
          if (res.status === 409 && error.detail?.conflicts) {
            errorMsg = error.detail.conflicts.join('\n');
            toast.error(`Conflitto: ${error.detail.message}`, { duration: 8000 });
            error.detail.conflicts.forEach(c => toast.warning(c, { duration: 8000 }));
            return;
          }
          errorMsg = typeof error.detail === 'string' ? error.detail : JSON.stringify(error.detail || error).substring(0, 200);
          if (Array.isArray(error.detail)) {
            errorMsg = error.detail.map(e => `${e.loc?.join('.')}: ${e.msg}`).join(', ');
          }
        } catch { 
          errorMsg = await res.text().catch(() => errorMsg);
        }
        console.error('[SaveRow] Error:', res.status, errorMsg);
        toast.error(`Errore salvataggio: ${errorMsg}`);
      }
    } catch (e) {
      console.error('[SaveRow] Exception:', e);
      toast.error(`Errore: ${e.message}`);
    }
  };

  const deleteEntry = async (id) => {
    if (!window.confirm('Eliminare definitivamente questa riga? Questa azione è irreversibile.')) return;
    try {
      const res = await fetch(`${API}/api/booking-register/${id}`, {
        method: 'DELETE', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success('Eliminata'); fetchEntries(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch (e) { toast.error('Errore'); }
  };

  const cancelEntry = async (id, currentStatus) => {
    const isCancelled = currentStatus === 'cancelled';
    const msg = isCancelled ? 'Riattivare questa prenotazione?' : 'Annullare questa prenotazione? (rimarrà visibile in rosso)';
    if (!window.confirm(msg)) return;
    try {
      const res = await fetch(`${API}/api/booking-register/${id}/cancel`, {
        method: 'PUT', headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) { toast.success(isCancelled ? 'Prenotazione riattivata' : 'Prenotazione annullata'); fetchEntries(); }
      else { const err = await res.json(); toast.error(err.detail || 'Errore'); }
    } catch (e) { toast.error('Errore'); }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  // Sorting & filtering logic
  const toggleSort = (col) => {
    if (sortCol === col) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortCol(col);
      setSortDir('asc');
    }
  };

  const { activeEntries, cancelledEntries } = useMemo(() => {
    const active = entries.filter(e => e.status !== 'cancelled');
    const cancelled = entries.filter(e => e.status === 'cancelled');
    return { activeEntries: active, cancelledEntries: cancelled };
  }, [entries]);

  const sortEntries = useCallback((list) => {
    if (!sortCol) return list;
    return [...list].sort((a, b) => {
      let va = a[sortCol], vb = b[sortCol];
      if (typeof va === 'string') va = (va || '').toLowerCase();
      if (typeof vb === 'string') vb = (vb || '').toLowerCase();
      if (typeof va === 'number' || typeof vb === 'number') {
        va = parseFloat(va) || 0;
        vb = parseFloat(vb) || 0;
      }
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortCol, sortDir]);

  const sortedActive = useMemo(() => sortEntries(activeEntries), [activeEntries, sortEntries]);
  const sortedCancelled = useMemo(() => sortEntries(cancelledEntries), [cancelledEntries, sortEntries]);

  const SortableHeader = ({ col, children, className = '' }) => (
    <th 
      className={`px-1.5 py-2 font-semibold border-b border-r border-slate-200 cursor-pointer select-none hover:bg-slate-200 transition-colors ${className}`}
      onClick={() => toggleSort(col)}
      data-testid={`sort-${col}`}
    >
      <div className="flex items-center gap-0.5 justify-inherit">
        {children}
        {sortCol === col ? (
          sortDir === 'asc' ? <ArrowUp className="h-3 w-3 text-blue-600 flex-shrink-0" /> : <ArrowDown className="h-3 w-3 text-blue-600 flex-shrink-0" />
        ) : (
          <ArrowUpDown className="h-2.5 w-2.5 text-slate-300 flex-shrink-0" />
        )}
      </div>
    </th>
  );

  const fmtDate = (s) => { if (!s) return ''; const d = new Date(s); return `${d.getDate().toString().padStart(2,'0')}/${(d.getMonth()+1).toString().padStart(2,'0')}/${d.getFullYear().toString().slice(-2)}`; };

  // Conversione punto → virgola per display, virgola → punto per valore
  const handleDecimalInput = (value, field, isNew) => {
    // Converti punto in virgola per l'utente
    const displayVal = value.replace('.', ',');
    // Converti virgola in punto per il valore interno
    const internalVal = displayVal.replace(',', '.');
    if (isNew) updateNewRow(field, internalVal);
    else updateEditingRow(field, internalVal);
  };

  const formatDecimalDisplay = (val) => {
    if (val === '' || val === undefined || val === null) return '';
    return String(val).replace('.', ',');
  };

  // Coerce a value (number or numeric string, anche con virgola) in numero; fallback 0.
  // In modifica i campi importo vengono salvati come stringa (es. "778.50"), quindi
  // chiamare .toFixed() direttamente lanciava "toFixed is not a function".
  const toNum = (v) => {
    if (typeof v === 'number') return Number.isFinite(v) ? v : 0;
    const n = parseFloat(String(v ?? '').replace(',', '.'));
    return Number.isFinite(n) ? n : 0;
  };

  // Property dropdown cell - usa portal per renderizzare FUORI dalla tabella
  const PropertyCell = ({ rowData, isNew }) => {
    const btnRef = useRef(null);
    const rowKey = isNew ? 'new' : editingRow;
    const isOpen = propertyDropdownOpen === rowKey;
    const filtered = allProperties.filter(p => 
      !propertyFilter || p.name.toLowerCase().includes(propertyFilter.toLowerCase()) || 
      (p.property_code || '').toLowerCase().includes(propertyFilter.toLowerCase())
    );
    
    const openDropdown = () => {
      if (btnRef.current) {
        const rect = btnRef.current.getBoundingClientRect();
        setDropdownPos({
          top: rect.bottom + 4,
          left: rect.left,
          width: Math.max(rect.width, 350)
        });
      }
      setPropertyDropdownOpen(isOpen ? null : rowKey);
      setPropertyFilter('');
    };
    
    return (
      <div>
        <button
          ref={btnRef}
          type="button"
          className="w-full px-2 py-1 text-xs border rounded bg-white text-left flex items-center justify-between focus:outline-none focus:ring-1 focus:ring-blue-500"
          onClick={openDropdown}
          data-testid="property-select-btn"
        >
          <span className={rowData.property_name ? 'text-slate-800 truncate' : 'text-gray-400'}>
            {rowData.property_name || 'Seleziona immobile...'}
          </span>
          <ChevronDown className="h-3 w-3 text-gray-400 flex-shrink-0 ml-1" />
        </button>
      </div>
    );
  };

  // Dropdown portal - renderizzato a livello body
  const PropertyDropdownPortal = () => {
    const ref = useRef(null);
    const isOpen = propertyDropdownOpen !== null;
    const filtered = allProperties.filter(p => 
      !propertyFilter || p.name.toLowerCase().includes(propertyFilter.toLowerCase()) || 
      (p.property_code || '').toLowerCase().includes(propertyFilter.toLowerCase())
    );
    const isNew = propertyDropdownOpen === 'new';

    useEffect(() => {
      const handler = (e) => {
        if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[data-testid="property-select-btn"]')) {
          setPropertyDropdownOpen(null);
          setPropertyFilter('');
        }
      };
      if (isOpen) document.addEventListener('mousedown', handler);
      return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    if (!isOpen) return null;

    return ReactDOM.createPortal(
      <div
        ref={ref}
        className="bg-white border rounded-lg shadow-2xl z-[99999]"
        style={{
          position: 'fixed',
          top: dropdownPos.top,
          left: dropdownPos.left,
          width: dropdownPos.width,
        }}
      >
        <div className="p-2 border-b">
          <input
            type="text"
            className="w-full px-2 py-1.5 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
            placeholder="Filtra per nome o codice..."
            value={propertyFilter}
            onChange={(e) => setPropertyFilter(e.target.value)}
            autoFocus
            data-testid="property-filter-input"
          />
          <div className="text-xs text-gray-400 mt-1 px-1">{allProperties.length} immobili disponibili</div>
        </div>
        <div className="max-h-56 overflow-auto">
          {filtered.length === 0 ? (
            <div className="px-3 py-3 text-xs text-gray-400 text-center">Nessun risultato</div>
          ) : (
            filtered.map(p => (
              <div key={p.id} 
                className="px-3 py-2 text-xs hover:bg-blue-50 cursor-pointer border-b last:border-0"
                onClick={() => selectProperty(p, isNew)}
                data-testid={`property-option-${p.id}`}
              >
                <span className="font-medium">{p.name}</span>
                <span className="text-gray-400 ml-2">({p.property_code})</span>
                <span className="text-gray-400 ml-2">Max: {p.max_guests}</span>
              </div>
            ))
          )}
        </div>
      </div>,
      document.body
    );
  };

  const SelectCell = ({ value, field, isNew, options, className = '' }) => {
    const isEditing = isNew || editingRow !== null;
    return (
      <select className={`w-full px-1 py-1 text-xs border rounded focus:outline-none bg-white ${className}`}
        value={value || ''} onChange={(e) => isNew ? updateNewRow(field, e.target.value) : updateEditingRow(field, e.target.value)}
        disabled={!isEditing}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
  };

  const SubmultipleCell = ({ value, rowData, isNew }) => {
    // Enrich with property submultiple config if not present in row data
    let enrichedData = rowData;
    if (rowData.property_id && rowData.apply_submultiples === undefined) {
      const prop = allProperties.find(p => p.id === rowData.property_id);
      if (prop) {
        enrichedData = {
          ...rowData,
          apply_submultiples: prop.apply_submultiples || false,
          available_submultiples: prop.available_submultiples || []
        };
      }
    }
    const options = getSubmultipleOptions(enrichedData);
    if (!rowData.property_id) return <span className="text-xs text-gray-400 block text-center italic">Selez. immobile</span>;
    if (options.length === 0) return <span className="text-xs text-gray-400 block text-center">-</span>;
    return <SelectCell value={value} field="submultiple" isNew={isNew} options={options} />;
  };

  const renderRow = (data, idx, isNew = false) => {
    const isEditing = isNew || editingRow === idx;
    const sub = data.submultiple ? `/S${data.submultiple}` : (data.max_guests ? `${data.max_guests}` : '-');
    const isCancelled = data.status === 'cancelled';
    
    const cellCls = `px-1.5 py-1.5 border-r border-slate-200 ${isCancelled ? 'line-through opacity-60' : ''}`;
    
    return (
      <tr key={isNew ? 'new' : data.id} className={`border-b ${isNew ? 'bg-blue-50' : isCancelled ? 'bg-red-50' : data.has_anomaly ? 'bg-red-50' : 'hover:bg-gray-50'}`}
        data-testid={isNew ? 'new-booking-row' : `booking-row-${data.id}`}
        onKeyDown={(e) => { if (isEditing && e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); saveRow(data, isNew); } }}>
        {/* IMMOBILE */}
        <td className={`px-1.5 py-1.5 border-r border-slate-200 min-w-[140px] ${isCancelled ? 'line-through opacity-60' : ''}`}>
          {isEditing ? <PropertyCell rowData={data} isNew={isNew} /> : (
            <div className="flex items-center gap-1">
              {isCancelled && <Ban className="h-3 w-3 text-red-500 flex-shrink-0" />}
              <span className="text-xs font-medium block truncate" title={data.property_name}>{data.property_name}</span>
            </div>
          )}
        </td>
        {/* N° PREN */}
        <td className={`${cellCls} min-w-[110px]`}>
          {isEditing ? <input type="text" className="w-full px-2 py-1 text-xs border rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={data.booking_code || ''} placeholder="Codice prenotazione..." onChange={(e) => isNew ? updateNewRow('booking_code', e.target.value) : updateEditingRow('booking_code', e.target.value)} data-testid="input-booking-code" /> : (
            <span className="font-mono text-xs">{data.booking_code}</span>
          )}
        </td>
        {/* N/R */}
        <td className={`${cellCls} w-14 text-center`}>
          {isEditing ? <SelectCell value={data.tariff_type} field="tariff_type" isNew={isNew} options={[{value:'N',label:'N'},{value:'R',label:'R'}]} /> : (
            <Badge variant="outline" className={`text-xs px-1 ${data.tariff_type === 'N' ? 'bg-orange-100' : 'bg-green-100'}`}>{data.tariff_type}</Badge>
          )}
        </td>
        {/* PERS (sottomultipli) */}
        <td className={`${cellCls} w-20 text-center`}>
          {isEditing ? <SubmultipleCell value={data.submultiple} rowData={data} isNew={isNew} /> : (
            <span className="text-xs">{sub}</span>
          )}
        </td>
        {/* PIATT */}
        <td className={`${cellCls} w-20`}>
          {isEditing ? <SelectCell value={data.platform} field="platform" isNew={isNew} options={PLATFORMS} /> : (
            <span className="text-xs">{data.platform?.replace('_',' ')}</span>
          )}
        </td>
        {/* DAL */}
        <td className={`${cellCls} w-24 text-center`}>
          {isEditing ? <input type="date" className="w-full px-1 py-1 text-xs border rounded bg-white" value={data.check_in || ''} onChange={(e) => isNew ? updateNewRow('check_in', e.target.value) : updateEditingRow('check_in', e.target.value)} /> : (
            <span className="text-xs">{fmtDate(data.check_in)}</span>
          )}
        </td>
        {/* AL */}
        <td className={`${cellCls} w-24 text-center`}>
          {isEditing ? <input type="date" className="w-full px-1 py-1 text-xs border rounded bg-white" value={data.check_out || ''} onChange={(e) => isNew ? updateNewRow('check_out', e.target.value) : updateEditingRow('check_out', e.target.value)} /> : (
            <span className="text-xs">{fmtDate(data.check_out)}</span>
          )}
        </td>
        {/* INCASSO (data incasso) */}
        <td className={`${cellCls} w-24 text-center`}>
          {isEditing ? <input type="date" className="w-full px-1 py-1 text-xs border rounded bg-white border-amber-300 focus:ring-amber-400" value={data.collection_date || ''} onChange={(e) => isNew ? updateNewRow('collection_date', e.target.value) : updateEditingRow('collection_date', e.target.value)} data-testid="input-collection-date" /> : (
            <span className={`text-xs ${data.collection_date && data.collection_date <= new Date().toISOString().split('T')[0] ? 'font-semibold text-amber-700' : ''}`}>{fmtDate(data.collection_date)}</span>
          )}
        </td>
        {/* LOCAZIONE */}
        <td className={`${cellCls} w-20 text-right`}>
          {isEditing ? <input type="text" inputMode="decimal" className="w-full px-2 py-1 text-xs border rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={formatDecimalDisplay(data.total_price)} onChange={(e) => handleDecimalInput(e.target.value, 'total_price', isNew)} data-testid="input-total-price" /> : (
            <span className="text-xs">{data.total_price ? String(data.total_price.toFixed(2)).replace('.', ',') : '0,00'}</span>
          )}
        </td>
        {/* TOTALE (locazione + pulizie per HW/Interhome/Atraveo dal 02/04) */}
        <td className={`${cellCls} w-20 text-right bg-blue-50/30`}>
          <span className="text-xs font-medium text-blue-800">{(toNum(data.total_price_full) || toNum(data.total_price)).toFixed(2).replace('.', ',')}</span>
        </td>
        {/* PRESI */}
        <td className={`${cellCls} w-20 text-right`}>
          {isEditing ? <input type="text" inputMode="decimal" className="w-full px-2 py-1 text-xs border rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={formatDecimalDisplay(data.collected)} onChange={(e) => handleDecimalInput(e.target.value, 'collected', isNew)} data-testid="input-collected" /> : (
            <span className="text-xs">{data.collected ? String(data.collected.toFixed(2)).replace('.', ',') : '0,00'}</span>
          )}
        </td>
        {/* DA PREN - sempre derivato: max(0, TOTALE - PRESI) per evitare dati storici stale nel DB */}
        <td className={`${cellCls} w-20 text-right`}>
          {(() => {
            // Usa total_price (prezzo all'ospite), NON total_price_full che include surcharge pulizie non incassate
            const total = parseFloat(data.total_price) || 0;
            const collectedAmount = parseFloat(data.collected) || 0;
            const derivedToCollect = Math.max(0, Math.round((total - collectedAmount) * 100) / 100);
            return isEditing ? (
              <input type="text" inputMode="decimal" className="w-full px-2 py-1 text-xs border rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={formatDecimalDisplay(derivedToCollect)} readOnly tabIndex={-1} data-testid="input-to-collect" title="Calcolato automaticamente: TOTALE - PRESI" />
            ) : (
              <span className="text-xs" data-testid="cell-to-collect">{derivedToCollect ? String(derivedToCollect.toFixed(2)).replace('.', ',') : '0,00'}</span>
            );
          })()}
        </td>
        {/* PROP */}
        <td className={`${cellCls} w-20 text-right`}>
          {isEditing ? <input type="text" inputMode="decimal" className="w-full px-2 py-1 text-xs border rounded text-right focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white" value={formatDecimalDisplay(data.owner_share)} onChange={(e) => handleDecimalInput(e.target.value, 'owner_share', isNew)} data-testid="input-owner-share" /> : (
            <span className="text-xs">{data.owner_share ? String(data.owner_share.toFixed(2)).replace('.', ',') : '0,00'}</span>
          )}
        </td>
        {/* PUL */}
        <td className={`${cellCls} w-28 text-center`}>
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input type="text" inputMode="decimal" className="w-16 px-1 py-1 text-xs border rounded text-right bg-white" value={formatDecimalDisplay(data.cleaning_cost)} onChange={(e) => handleDecimalInput(e.target.value, 'cleaning_cost', isNew)} />
              <select className="w-14 px-0.5 py-1 text-xs border rounded bg-white" value={data.cleaning_where || 'in_total'} onChange={(e) => isNew ? updateNewRow('cleaning_where', e.target.value) : updateEditingRow('cleaning_where', e.target.value)}>
                <option value="in_total">Tot</option>
                <option value="on_site">Posto</option>
              </select>
            </div>
          ) : (
            <span className="text-xs">{data.cleaning_cost ? String(data.cleaning_cost.toFixed(2)).replace('.', ',') : '0,00'}{data.cleaning_where === 'on_site' && <span className="text-orange-500">*</span>}</span>
          )}
        </td>
        {/* TASSA */}
        <td className={`${cellCls} w-28 text-center`}>
          {isEditing ? (
            <div className="flex items-center gap-1">
              <input type="text" inputMode="decimal" className="w-16 px-1 py-1 text-xs border rounded text-right bg-white" value={formatDecimalDisplay(data.tourist_tax)} onChange={(e) => handleDecimalInput(e.target.value, 'tourist_tax', isNew)} />
              <select className="w-14 px-0.5 py-1 text-xs border rounded bg-white" value={data.tourist_tax_where || 'in_total'} onChange={(e) => isNew ? updateNewRow('tourist_tax_where', e.target.value) : updateEditingRow('tourist_tax_where', e.target.value)}>
                <option value="in_total">Tot</option>
                <option value="on_site">Posto</option>
              </select>
            </div>
          ) : (
            <span className="text-xs">{data.tourist_tax ? String(data.tourist_tax.toFixed(2)).replace('.', ',') : '0,00'}{data.tourist_tax_where === 'on_site' && <span className="text-orange-500">*</span>}</span>
          )}
        </td>
        {/* Admin columns */}
        {isAdmin && (
          <>
            <td className={`${cellCls} w-16 text-right`}>
              {isEditing && isNew ? <span className="text-xs text-gray-400">auto</span> : (
                <span className="text-xs text-gray-600">{data.card_fee ? String(data.card_fee.toFixed(2)).replace('.', ',') : '0,00'}</span>
              )}
            </td>
            <td className={`${cellCls} w-16 text-right`}>
              {isEditing && isNew ? <span className="text-xs text-gray-400">auto</span> : (
                <span className="text-xs text-blue-600">{data.platform_fee ? String(data.platform_fee.toFixed(2)).replace('.', ',') : '0,00'}</span>
              )}
            </td>
            <td className={`${cellCls} w-16 text-right`}>
              {isEditing && isNew ? <span className="text-xs text-gray-400">auto</span> : (
                <span className="text-xs text-purple-600">{data.partner_fee ? String(data.partner_fee.toFixed(2)).replace('.', ',') : '0,00'}</span>
              )}
            </td>
            <td className={`${cellCls} w-16 text-right`}>
              {isEditing && isNew ? <span className="text-xs text-gray-400">auto</span> : (
                <span className={`text-xs font-bold ${(data.hw_margin || 0) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{data.hw_margin ? String(data.hw_margin.toFixed(2)).replace('.', ',') : '0,00'}</span>
              )}
            </td>
          </>
        )}
        {/* Actions */}
        <td className="px-1.5 py-1.5 text-center">
          <div className="flex items-center justify-center gap-1">
            {data.has_anomaly && !isEditing && !isCancelled && <AlertTriangle className="h-3 w-3 text-red-500" />}
            {isEditing ? (
              <>
                <button type="button" onClick={() => saveRow(data, isNew)} className="p-1 hover:bg-green-100 rounded" title="Salva" data-testid="save-row-btn">
                  <Save className="h-3.5 w-3.5 text-green-600" />
                </button>
                <button type="button" onClick={() => { if (isNew) setNewRow(null); else { setEditingRow(null); fetchEntries(); }}} className="p-1 hover:bg-gray-100 rounded" title="Annulla modifica">
                  <X className="h-3.5 w-3.5 text-gray-500" />
                </button>
              </>
            ) : (
              <>
                {!isCancelled && (
                  <button onClick={() => setEditingRow(idx)} className="p-1 hover:bg-blue-100 rounded" title="Modifica" data-testid={`edit-row-${data.id}`}>
                    <Edit2 className="h-3.5 w-3.5 text-blue-600" />
                  </button>
                )}
                {/* Cancel/Restore - per tutti i ruoli autorizzati */}
                <button 
                  onClick={() => cancelEntry(data.id, data.status)} 
                  className={`p-1 rounded ${isCancelled ? 'hover:bg-green-100' : 'hover:bg-orange-100'}`} 
                  title={isCancelled ? 'Riattiva prenotazione' : 'Annulla prenotazione'}
                  data-testid={`cancel-row-${data.id}`}
                >
                  {isCancelled ? <RotateCcw className="h-3.5 w-3.5 text-green-600" /> : <Ban className="h-3.5 w-3.5 text-orange-500" />}
                </button>
                {/* Delete - solo admin */}
                {isAdmin && (
                  <button onClick={() => deleteEntry(data.id)} className="p-1 hover:bg-red-100 rounded" title="Elimina definitivamente" data-testid={`delete-row-${data.id}`}>
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </button>
                )}
              </>
            )}
          </div>
        </td>
      </tr>
    );
  };

  const totalCols = isAdmin ? 20 : 16;

  return (
    <div className="p-4 space-y-4" data-testid="registro-prenotazioni">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold text-slate-800">Registro Prenotazioni</h1>
          <p className="text-xs text-slate-500">Inserisci le prenotazioni come nel foglio Excel</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => changeDate(-1)}><ChevronLeft className="h-4 w-4" /></Button>
          <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-36 h-8 text-sm" />
          <Button variant="outline" size="sm" onClick={() => changeDate(1)}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" onClick={fetchEntries}><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Table */}
      <Card>
        <CardHeader className="py-2 px-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              {new Date(selectedDate).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={() => { setNewRow({ ...emptyRow }); setEditingRow(null); }} disabled={newRow !== null} data-testid="new-row-btn">
                <Plus className="h-3 w-3 mr-1" /> Nuova Riga
              </Button>
              <Button 
                size="sm" 
                variant={showCancelled ? "destructive" : "outline"}
                onClick={() => setShowCancelled(!showCancelled)}
                data-testid="toggle-cancelled-btn"
                className={!showCancelled ? "border-red-200 text-red-600 hover:bg-red-50" : ""}
              >
                {showCancelled ? <EyeOff className="h-3 w-3 mr-1" /> : <Eye className="h-3 w-3 mr-1" />}
                Annullate {cancelledEntries.length > 0 && `(${cancelledEntries.length})`}
              </Button>
            </div>
            {isAdmin && (
              <div className="flex items-center gap-2">
                <select
                  className="border rounded px-2 py-1 text-xs bg-white"
                  value={dateMode}
                  onChange={e => setDateMode(e.target.value)}
                  data-testid="date-mode-select"
                >
                  <option value="booking_date">Per data prenotazione</option>
                  <option value="check_in">Per data check-in</option>
                </select>
                <Button size="sm" variant="outline" onClick={populateFromBookings} disabled={populating} data-testid="populate-from-bookings-btn">
                  {populating ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                  Popola da Booking
                </Button>
                <Button size="sm" variant="outline" className="text-orange-600 border-orange-300 hover:bg-orange-50" onClick={async () => {
                  if (!window.confirm('Eseguire la deduplicazione del registro? Le entry duplicate verranno rimosse mantenendo la più recente.')) return;
                  try {
                    const res = await fetch(`${API}/api/booking-register/dedup?dry_run=false`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` }
                    });
                    const data = await res.json();
                    if (data.entries_removed > 0) {
                      toast.success(`Rimossi ${data.entries_removed} duplicati da ${data.duplicate_groups} gruppi`);
                      fetchEntries();
                    } else {
                      toast.info('Nessun duplicato trovato');
                    }
                  } catch (e) { toast.error('Errore dedup'); }
                }} data-testid="dedup-register-btn">
                  Rimuovi Duplicati
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8"><RefreshCw className="h-6 w-6 animate-spin text-blue-500" /></div>
          ) : (
            <div className="overflow-x-auto" style={{overflowY: 'visible'}}>
              <table className="w-full text-xs border-collapse min-w-[1200px]">
                <thead className="bg-slate-100">
                  <tr>
                    <SortableHeader col="property_name" className="text-left min-w-[140px]">IMMOBILE</SortableHeader>
                    <SortableHeader col="booking_code" className="text-left min-w-[110px]">N° PREN.</SortableHeader>
                    <SortableHeader col="tariff_type" className="text-center w-14">N/R</SortableHeader>
                    <SortableHeader col="submultiple" className="text-center w-20">PERS.</SortableHeader>
                    <SortableHeader col="platform" className="text-center w-20">PIATT.</SortableHeader>
                    <SortableHeader col="check_in" className="text-center w-24">DAL</SortableHeader>
                    <SortableHeader col="check_out" className="text-center w-24">AL</SortableHeader>
                    <SortableHeader col="collection_date" className="text-center w-24 text-amber-700">INCASSO</SortableHeader>
                    <SortableHeader col="total_price" className="text-right w-20">LOCAZ.</SortableHeader>
                    <th className="px-2 py-1.5 text-right border-b border-r border-slate-200 text-xs font-semibold w-20 bg-blue-50 text-blue-800">TOTALE</th>
                    <SortableHeader col="collected" className="text-right w-20">PRESI</SortableHeader>
                    <SortableHeader col="to_collect" className="text-right w-20">DA PREN.</SortableHeader>
                    <SortableHeader col="owner_share" className="text-right w-20">PROP</SortableHeader>
                    <SortableHeader col="cleaning_cost" className="text-center w-28">PUL</SortableHeader>
                    <SortableHeader col="tourist_tax" className="text-center w-28">TASSA</SortableHeader>
                    {isAdmin && (
                      <>
                        <SortableHeader col="card_fee" className="text-right w-16 text-gray-500">CARTA</SortableHeader>
                        <SortableHeader col="platform_fee" className="text-right w-16 text-blue-600">SITO</SortableHeader>
                        <SortableHeader col="partner_fee" className="text-right w-16 text-purple-600">PARTNER</SortableHeader>
                        <SortableHeader col="hw_margin" className="text-right w-16 text-green-600">DIFF</SortableHeader>
                      </>
                    )}
                    <th className="px-1.5 py-2 text-center font-semibold border-b w-20"></th>
                  </tr>
                </thead>
                <tbody>
                  {newRow && renderRow(newRow, -1, true)}
                  {sortedActive.map((entry, idx) => renderRow(entry, entries.indexOf(entry)))}
                  {sortedActive.length === 0 && !newRow && (
                    <tr>
                      <td colSpan={totalCols} className="text-center py-8 text-gray-400">
                        Nessuna prenotazione per questa data. Clicca "Nuova Riga" per aggiungerne una.
                      </td>
                    </tr>
                  )}
                  {/* Sezione annullate */}
                  {showCancelled && sortedCancelled.length > 0 && (
                    <>
                      <tr className="bg-red-100 border-t-2 border-red-300">
                        <td colSpan={totalCols} className="px-3 py-2 text-xs font-bold text-red-700 flex items-center gap-2">
                          <Ban className="h-3.5 w-3.5" /> ANNULLATE ({sortedCancelled.length})
                        </td>
                      </tr>
                      {sortedCancelled.map((entry, idx) => renderRow(entry, entries.indexOf(entry)))}
                    </>
                  )}
                </tbody>
                {/* TOTALI - solo admin */}
                {isAdmin && entries.length > 0 && (() => {
                  const activeEntries = entries.filter(e => e.status !== 'cancelled');
                  const sumField = (arr, field) => arr.reduce((acc, e) => acc + (parseFloat(e[field]) || 0), 0);
                  // DA PREN sempre derivato: max(0, total_price - collected)
                  // NB: usare total_price (prezzo ospite), NON total_price_full che include surcharge pulizie
                  const sumToCollectDerived = (arr) => arr.reduce((acc, e) => {
                    const total = parseFloat(e.total_price) || 0;
                    const collectedAmount = parseFloat(e.collected) || 0;
                    return acc + Math.max(0, total - collectedAmount);
                  }, 0);
                  const fmt = (v) => v.toFixed(2).replace('.', ',');
                  const dailyPrice = sumField(activeEntries, 'total_price');
                  const dailyTotalFull = activeEntries.reduce((acc, e) => acc + (parseFloat(e.total_price_full) || parseFloat(e.total_price) || 0), 0);
                  const dailyCollected = sumField(activeEntries, 'collected');
                  const dailyToCollect = sumToCollectDerived(activeEntries);
                  const dailyOwner = sumField(activeEntries, 'owner_share');
                  const dailyCleaning = sumField(activeEntries, 'cleaning_cost');
                  const dailyTax = sumField(activeEntries, 'tourist_tax');
                  const dailyCard = sumField(activeEntries, 'card_fee');
                  const dailyPlatform = sumField(activeEntries, 'platform_fee');
                  const dailyPartner = sumField(activeEntries, 'partner_fee');
                  const dailyMargin = sumField(activeEntries, 'hw_margin');
                  const mt = monthlyTotals?.totals || {};
                  const mFmt = (v) => (v || 0).toFixed(2).replace('.', ',');
                  const emptyCols = 8; // IMMOBILE, N° PREN, N/R, PERS, PIATT, DAL, AL, INCASSO

                  return (
                    <tfoot>
                      {/* TOTALE GIORNALIERO */}
                      <tr className="bg-blue-50 border-t-2 border-blue-300" data-testid="daily-totals-row">
                        <td colSpan={emptyCols} className="px-2 py-1.5 text-xs font-bold text-blue-800 border-r border-blue-200">
                          TOTALE GIORNO ({activeEntries.length} pren.)
                        </td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200">{fmt(dailyPrice)}</td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200 bg-blue-100/50">{fmt(dailyTotalFull)}</td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200">{fmt(dailyCollected)}</td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200">{fmt(dailyToCollect)}</td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200">{fmt(dailyOwner)}</td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200">{fmt(dailyCleaning)}</td>
                        <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-800 border-r border-blue-200">{fmt(dailyTax)}</td>
                        {isAdmin && (
                          <>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-gray-600 border-r border-blue-200">{fmt(dailyCard)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-700 border-r border-blue-200">{fmt(dailyPlatform)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-purple-700 border-r border-blue-200">{fmt(dailyPartner)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-green-700 border-r border-blue-200">{fmt(dailyMargin)}</td>
                          </>
                        )}
                        <td className="border-blue-200"></td>
                      </tr>
                      {/* PROGRESSIVO MENSILE */}
                      {monthlyTotals && (
                        <tr className="bg-amber-50 border-t border-amber-300" data-testid="monthly-totals-row">
                          <td colSpan={emptyCols} className="px-2 py-1.5 text-xs font-bold text-amber-800 border-r border-amber-200">
                            PROGRESSIVO {selectedDate.substring(0, 7)} ({mt.count || 0} pren.)
                          </td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200">{mFmt(mt.total_price)}</td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200 bg-blue-50/30">{mFmt(mt.total_price_full || mt.total_price)}</td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200">{mFmt(mt.collected)}</td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200">{mFmt(mt.to_collect)}</td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200">{mFmt(mt.owner_share)}</td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200">{mFmt(mt.cleaning_cost)}</td>
                          <td className="px-1.5 py-1.5 text-right text-xs font-bold text-amber-800 border-r border-amber-200">{mFmt(mt.tourist_tax)}</td>
                          {isAdmin && (
                            <>
                              <td className="px-1.5 py-1.5 text-right text-xs font-bold text-gray-600 border-r border-amber-200">{mFmt(mt.card_fee)}</td>
                              <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-700 border-r border-amber-200">{mFmt(mt.platform_fee)}</td>
                              <td className="px-1.5 py-1.5 text-right text-xs font-bold text-purple-700 border-r border-amber-200">{mFmt(mt.partner_fee)}</td>
                              <td className="px-1.5 py-1.5 text-right text-xs font-bold text-green-700 border-r border-amber-200">{mFmt(mt.hw_margin)}</td>
                            </>
                          )}
                          <td className="border-amber-200"></td>
                        </tr>
                      )}
                      {/* PROGRESSIVO ANNUO */}
                      {yearlyTotals && (() => {
                        const yt = yearlyTotals.totals || {};
                        return (
                          <tr className="bg-emerald-50 border-t border-emerald-300" data-testid="yearly-totals-row">
                            <td colSpan={emptyCols} className="px-2 py-1.5 text-xs font-bold text-emerald-800 border-r border-emerald-200">
                              PROGRESSIVO ANNO {selectedDate.substring(0, 4)} ({yt.count || 0} pren.)
                            </td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200">{mFmt(yt.total_price)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200 bg-blue-50/30">{mFmt(yt.total_price_full || yt.total_price)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200">{mFmt(yt.collected)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200">{mFmt(yt.to_collect)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200">{mFmt(yt.owner_share)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200">{mFmt(yt.cleaning_cost)}</td>
                            <td className="px-1.5 py-1.5 text-right text-xs font-bold text-emerald-800 border-r border-emerald-200">{mFmt(yt.tourist_tax)}</td>
                            {isAdmin && (
                              <>
                                <td className="px-1.5 py-1.5 text-right text-xs font-bold text-gray-600 border-r border-emerald-200">{mFmt(yt.card_fee)}</td>
                                <td className="px-1.5 py-1.5 text-right text-xs font-bold text-blue-700 border-r border-emerald-200">{mFmt(yt.platform_fee)}</td>
                                <td className="px-1.5 py-1.5 text-right text-xs font-bold text-purple-700 border-r border-emerald-200">{mFmt(yt.partner_fee)}</td>
                                <td className="px-1.5 py-1.5 text-right text-xs font-bold text-green-700 border-r border-emerald-200">{mFmt(yt.hw_margin)}</td>
                              </>
                            )}
                            <td className="border-emerald-200"></td>
                          </tr>
                        );
                      })()}
                    </tfoot>
                  );
                })()}
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legenda sotto la tabella */}
      <div className="px-4 py-2 text-xs text-slate-500 bg-slate-50 rounded border">
        <span className="text-orange-500">*</span> = Sul posto | 
        <Save className="h-3 w-3 inline ml-2" /> Salva |
        <Edit2 className="h-3 w-3 inline ml-2" /> Modifica |
        <Ban className="h-3 w-3 inline ml-2 text-orange-500" /> Annulla |
        <RotateCcw className="h-3 w-3 inline ml-2 text-green-600" /> Riattiva
        {isAdmin && <span className="ml-2">| <Trash2 className="h-3 w-3 inline text-red-500" /> Elimina</span>}
      </div>

      {/* Sezione Anomalie Finanziarie - solo admin */}
      {isAdmin && <AnomaliesSection token={token} year={selectedDate.substring(0, 4)} />}
      <DaIncassareSection token={token} isAdmin={isAdmin} />

      {/* Portal per dropdown immobili - renderizzato fuori dalla tabella */}
      <PropertyDropdownPortal />
    </div>
  );
};

export default RegistroPrenotazioni;
